// Case types: what the customer's question is about, and where its errand goes.

import React from 'react';
import { I } from '../../../common/v5/config';
import { addCaseType, cleanKey, moveItem, parseTags, problemFor, removeCaseType } from './definition';
import { Field, LangText, MoveRemove, Section, Switch } from './ui';

export default function CaseTypesTab({ defn, lang, problems, areas, onChange }) {
	const main = defn.default_language;

	const update = (index, patch) => {
		const case_types = defn.case_types.map((c, i) => (i === index ? Object.assign({}, c, patch) : c));
		onChange(Object.assign({}, defn, { case_types }));
	};

	return (
		<div className="aif-stack">
			<p className="aif-muted">
				{I("Customers start by choosing what their question is about. Each choice sends its errand to an area, with a priority and tags. With a single case type, the choice isn't shown.")}
			</p>
			{defn.case_types.map((ct, index) => {
				const path = "case_types[" + index + "]";
				const areaKnown = areas.some(a => a.id === ct.area_id);
				const areaError = problemFor(problems, path + ".area_id")
					|| (areas.length && !areaKnown ? I("This area no longer exists or you can't route to it. Choose another.") : undefined);
				return (
					<Section
						key={index}
						title={ct.label[lang] || ct.label[main] || ct.key}
						action={
							<MoveRemove
								index={index}
								count={defn.case_types.length}
								onMove={d => onChange(Object.assign({}, defn, { case_types: moveItem(defn.case_types, index, d) }))}
								removeLabel={I("Remove case type")}
								canRemove={defn.case_types.length > 1}
								onRemove={() => {
									if (window.confirm(I("Remove this case type? Fields shown only for it are removed too."))) {
										onChange(removeCaseType(defn, ct.key));
									}
								}}
							/>
						}
					>
						<div className="aif-grid2">
							<LangText label={I("Name shown to customers")} value={ct.label} lang={lang} mainLang={main}
								onChange={label => update(index, { label })} error={problemFor(problems, path + ".label")} />
							<Field label={I("Area")} error={areaError}>
								{id => (
									<select id={id} value={ct.area_id} onChange={e => update(index, { area_id: e.target.value })}>
										{!areaKnown && <option value={ct.area_id}>{ct.area_id ? "#" + ct.area_id : I("Choose an area")}</option>}
										{areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
									</select>
								)}
							</Field>
							<Field label={I("Priority")} error={problemFor(problems, path + ".priority")}>
								{id => (
									<select id={id}
										value={ct.priority === "high" || ct.priority === "urgent" ? "high" : "normal"}
										onChange={e => update(index, { priority: e.target.value })}>
										<option value="normal">{I("Normal")}</option>
										<option value="high">{I("High")}</option>
									</select>
								)}
							</Field>
							<Field label={I("Tags")} hint={I("Comma-separated, added to every errand of this type.")} error={problemFor(problems, path + ".tags")}>
								{id => (
									<input id={id} type="text"
										key={index + ":" + ct.tags.join(",")}
										defaultValue={ct.tags.join(", ")}
										onBlur={e => update(index, { tags: parseTags(e.target.value) })} />
								)}
							</Field>
						</div>
						<LangText label={I("Intro (optional)")} hint={I("Shown under the choice, e.g. “Have your order number ready.”")}
							value={ct.intro} lang={lang} mainLang={main} multiline
							onChange={intro => update(index, { intro })} error={problemFor(problems, path + ".intro")} />
						<div className="aif-spread">
							<Switch checked={ct.ai_enabled} onChange={ai_enabled => update(index, { ai_enabled })}
								label={I("AI answers for this type")}
								description={I("Turn off for questions that always need a person, like a return of a specific order.")} />
							<Field label={I("Key")} error={problemFor(problems, path + ".key")}>
								{id => (
									<input id={id} type="text" className="aif-mono aif-key" value={ct.key}
										title={I("Used in links (?type=key) and in the errand's form details.")}
										onChange={e => update(index, { key: cleanKey(e.target.value) })} />
								)}
							</Field>
						</div>
					</Section>
				);
			})}
			<button type="button" className="aif-add" disabled={defn.case_types.length >= 20}
				onClick={() => onChange(addCaseType(defn, (defn.case_types[0] && defn.case_types[0].area_id) || (areas[0] && areas[0].id) || ""))}>
				<i className="fa fa-plus" aria-hidden="true" /> {I("Add case type")}
			</button>
		</div>
	);
}
